import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CartSlider } from 'components/CartSlider';
import * as Styled from './styles';
import { getRawCollectionList } from 'appRedux/restApi';
import { arrayOfLength } from 'utils/array-methods/array-of-length';
import { ReactQueryKeys } from 'constants/app/react-query-keys';
import { IGetCollectionParams, RestBoolean } from 'interfaces';

enum CollectionCarouselType {
  LATEST = 'latest',
  FEATURED = 'featured',
  TOP = 'top'
}

type CollectionsCarouselProps = {
  configs?: {
    collectionType?: CollectionCarouselType;
    limit?: number;
    innerShadowLeft?: boolean;
    innerShadowRight?: boolean;
  };
};

const CardStyles = {
  width: 288,
  height: 232
};

const SKELETON_COUNT = 5;

export const CollectionsCarousel: FC<CollectionsCarouselProps> = ({
  configs: _configs
}) => {
  const { t } = useTranslation();

  const configs = useMemo(() => {
    return {
      type: _configs?.collectionType || CollectionCarouselType.TOP,
      limit: _configs?.limit || 10,
      innerShadowLeft: _configs?.innerShadowLeft || false,
      innerShadowRight: _configs?.innerShadowRight || false
    };
  }, [_configs]);

  const params: Partial<IGetCollectionParams> = useMemo(() => {
    const _params: Partial<IGetCollectionParams> = {
      limit: configs.limit
    };
    if (configs.type === CollectionCarouselType.LATEST) {
      _params.sort = 'created_at';
      _params.order = 'desc';
    } else if (configs.type === CollectionCarouselType.FEATURED) {
      _params.is_featured = RestBoolean.True;
    } else if (configs.type === CollectionCarouselType.TOP) {
      _params.sort = 'total_supply';
      _params.order = 'desc';
    }
    return _params;
  }, [configs]);

  const { isLoading, data } = useQuery({
    queryKey: [ReactQueryKeys.COLLECTIONS_SLIDER, configs.type, configs.limit],
    queryFn: () => getRawCollectionList(params)
  });

  const collections = data?.data?.data || [];

  if (!isLoading && collections.length === 0) {
    return (
      <Styled.EmptyCollectionsSlider $height={CardStyles.height}>
        {t('collection.noCollections')}
      </Styled.EmptyCollectionsSlider>
    );
  }

  return (
    <CartSlider
      height={CardStyles.height}
      itemWidth={CardStyles.width}
      itemsCount={isLoading ? SKELETON_COUNT : collections.length}
      sliderProps={{
        arrows: true
      }}
      innerShadow={{
        left: configs.innerShadowLeft,
        right: configs.innerShadowRight
      }}
    >
      {isLoading &&
        arrayOfLength(SKELETON_COUNT * 2).map(key => (
          <Styled.CollectionCardSkeleton
            key={key}
            width={CardStyles.width}
            height={CardStyles.height}
          />
        ))}
      {!isLoading &&
        collections.length > 0 &&
        collections.map(collection => (
          <Styled.CollectionCard
            key={collection.id}
            collection={collection}
            width={CardStyles.width}
            height={CardStyles.height}
          />
        ))}
    </CartSlider>
  );
};
